import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { shareReplay, tap } from 'rxjs/operators';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { all, Theme } from './themeInterface';

interface VersionData {
  version: string;
}

export interface Xsaas {
  hostname: string;
  theme: Theme;
  active: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private rendererFactory = inject(RendererFactory2);

  private renderer: Renderer2;
  private currentTheme: 'light' | 'dark' = 'light';
  head = document.getElementsByTagName('head')[0];
  active: Theme;
  availableThemes: Xsaas[] = [
    { hostname: 'all', theme: all, active: true },
    // { hostname: 'localhost', theme: xfarm, active: true },
    // { hostname: 'xfarm-dev.xfarm.ag', theme: xfarm, active: true },
    // { hostname: 'cobo-dev.xfarm.ag', theme: cobo, active: true  },
    // { hostname: 'goldoni-dev.xfarm.ag', theme: goldoni, active: true  }
  ];
  themeConfigLocal = ThemeConfig;
  themeConfig;
  environmentConfig;
  private readonly VERSION_URL = 'assets/version.json?v=' + Math.random();
  private versionData: { version: string } | null = null;
  private versionRequest$: Observable<VersionData> | null = null;

  constructor(
    private meta: Meta,
    private titleService: Title,
    private http: HttpClient
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  getVersion(): Observable<VersionData> {
    // If already loaded return a static observable
    if (this.versionData) {
      return of(this.versionData);
    }

    if (!this.versionRequest$) {
      this.versionRequest$ = this.http
        .get<VersionData>(this.VERSION_URL, {
          headers: new HttpHeaders({ 'X-Skip-Interceptor': 'true' }),
        })
        .pipe(
          tap((data) => {
            this.versionData = data;
            localStorage.setItem('appVersion', this.versionData?.version);
          }),
          shareReplay(1)
        );
    }

    return this.versionRequest$;
  }

  getLocalVersion(clean = false): string {
    const version = localStorage.getItem('appVersion') ?? '0.0.0';
    if (clean) {
      return version.split('|')[0].trim();
    }
    return version;
  }

  getLocalVersionDate(): string {
    const version = localStorage.getItem('appVersion') ?? '0.0.0 | unknown';
    const parts = version.split('|');
    return parts[1]?.trim() ?? 'unknown';
  }

  setVersionInLocalStorage = (version) => {
    localStorage.setItem('appVersion', version);
  };

  setThemeBySaas(theme: Theme): void {
    this.active = theme;
    Object.keys(this.active.properties).forEach((property) => {
      document.documentElement.style.setProperty(property, this.active.properties[property]);
    });
  }

  getThemeBySaas(saas = 'all') {
    return this.availableThemes.find((theme) => theme.hostname === saas);
  }

  getConfigurationTheme() {
    return this.getThemeBySaas();
  }

  getValueFromConfigurationThemeByKey(key: string) {
    let value = null;
    const themeSaaSConfig = this.getConfigurationTheme().theme;
    if (themeSaaSConfig && themeSaaSConfig.properties) {
      const fields = key.split('.'); // dividi il campo in sotto-campi
      if (fields && fields.length == 1) {
        value = themeSaaSConfig.properties[fields[0]];
      } else if (fields && fields.length == 2) {
        value = themeSaaSConfig.properties[fields[0]][fields[1]];
      } else if (fields && fields.length == 3) {
        value = themeSaaSConfig.properties[fields[0]][fields[1]][fields[2]];
      }
    }
    return value;
  }

  setLinkRelsInHeader() {
    this.setLinkRelInHeader(
      'icon',
      'image/x-icon',
      null,
      this.getValueFromConfigurationThemeByKey('webElements.favicon')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '57x57',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon57x57')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '60x60',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon60x60')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '72x72',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon72x72')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '76x76',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon76x76')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '114x114',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon114x114')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '120x120',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon120x120')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '144x144',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon144x144')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '152x152',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon152x152')
    );
    this.setLinkRelInHeader(
      'apple-touch-icon',
      null,
      '180x180',
      this.getValueFromConfigurationThemeByKey('webElements.appleIcon180x180')
    );
    this.setLinkRelInHeader(
      'icon',
      'image/png',
      '192x192',
      this.getValueFromConfigurationThemeByKey('webElements.androidIcon192x192')
    );
    this.setLinkRelInHeader(
      'icon',
      'image/png',
      '32x32',
      this.getValueFromConfigurationThemeByKey('webElements.favicon32x32')
    );
    this.setLinkRelInHeader(
      'icon',
      'image/png',
      '96x96',
      this.getValueFromConfigurationThemeByKey('webElements.favicon96x96')
    );
    this.setLinkRelInHeader(
      'icon',
      'image/png',
      '16x16',
      this.getValueFromConfigurationThemeByKey('webElements.favicon16x16')
    );
    this.setLinkRelInHeader(
      'manifest',
      null,
      null,
      this.getValueFromConfigurationThemeByKey('webElements.manifestJson')
    );
  }

  setMetaTAGsInHeader() {
    if (this.themeConfigLocal.brand.toLowerCase() === 'xfarm') {
      this.meta.addTag({
        name: 'apple-itunes-app',
        content:
          'app-id=' +
          this.themeConfigLocal.iOSAPPIDCode +
          ', app-argument=' +
          this.themeConfigLocal.downlaodAPPUrl,
      });
    }
    this.meta.addTag({
      name: 'msapplication-TileImage',
      content: this.getValueFromConfigurationThemeByKey('webElements.msIcon144x144'),
    });
  }

  setTitleTagHeader() {
    this.titleService.setTitle(this.themeConfigLocal.textName + ' - SMART, SIMPLE, USEFUL');
  }

  setLinkRelInHeader(rel, type, sizes, href) {
    const item: any = document.createElement('link');
    if (rel) item.rel = rel;
    if (type) item.type = type;
    if (sizes) item.sizes = sizes;
    if (href) item.href = href;
    this.head.appendChild(item);
  }

  /** Sets the theme and persists it in localStorage */
  setTheme(theme: 'light' | 'dark'): void {
    if (this.currentTheme === theme) return; // avoid redundant re-apply

    this.currentTheme = theme;
    const html = document.documentElement;

    this.renderer.setAttribute(html, 'data-theme', theme);
    this.renderer[theme === 'dark' ? 'addClass' : 'removeClass'](html, 'p-dark');

    localStorage.setItem('xFarmTheme', theme);
  }

  toggleTheme(): void {
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /** Initializes the theme on app start */
  initTheme(): void {
    /*const detectSystemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';*/
    const saved = (localStorage.getItem('xFarmTheme') as 'light' | 'dark') || 'light'; //detectSystemTheme
    this.setTheme(saved);
  }

  /** Returns the currently active theme */
  getTheme(): 'light' | 'dark' {
    return this.currentTheme;
  }
}
